import React from 'react';
import {
    Switch,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Box,
    Typography,
    useTheme,
    SwitchProps,
} from '@mui/material';
import { Controller, Control } from 'react-hook-form';
import { SxProps, Theme } from '@mui/material/styles';

export interface InputSwitchControlledProps {
    title?: string;
    label?: string;
    endAdornment?: React.ReactNode;
    control: Control<any>;
    disabled?: boolean;
    name: string;
    defaultValue?: boolean;
    required?: boolean;
    containerStyle?: SxProps<Theme>;
    switchProps?: Partial<SwitchProps>;
    viewOnly?: boolean;
    onChange?: (value: boolean) => void;
    helperText?: string;
    error?: boolean;
    rules?: any
}

export default function InputSwitchControlled({
    title,
    label,
    endAdornment = null,
    control,
    disabled = false,
    name,
    defaultValue = false,
    required = false,
    containerStyle,
    switchProps = {},
    viewOnly = false,
    onChange,
    helperText,
    error = false,
    rules = {}
}: InputSwitchControlledProps) {
    const theme = useTheme();

    return (
        <Controller
            control={control}
            defaultValue={defaultValue}
            name={name}
            rules={{
                ...rules,
                required,
            }}
            render={({ field: { value, onChange: fieldOnChange }, fieldState: { error: fieldError } }) => {
                const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = event.target.checked;
                    fieldOnChange(newValue);
                    onChange?.(newValue);
                };

                if (viewOnly) {
                    return (
                        <FormGroup sx={{ ...containerStyle }}>
                            {(title || label) && (
                                <FormLabel>{title || label}</FormLabel>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: value
                                            ? theme.palette.success.main
                                            : theme.palette.text.disabled,
                                        fontWeight: 'medium',
                                    }}
                                >
                                    {value ? 'Activé' : 'Désactivé'}
                                </Typography>
                                {endAdornment}
                            </Box>
                        </FormGroup>
                    );
                }

                return (
                    <FormGroup sx={{ width: '100%', ...containerStyle }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(value)}
                                            onChange={handleChange}
                                            disabled={disabled}
                                            color="primary"
                                            sx={{
                                                transform: 'scale(0.8)',
                                                opacity: disabled ? 0.5 : 1,
                                            }}
                                            {...switchProps}
                                        />
                                    }
                                    label={
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: disabled
                                                    ? theme.palette.text.disabled
                                                    : theme.palette.text.primary,
                                                marginLeft: 1,
                                            }}
                                        >
                                            {title || label}
                                        </Typography>
                                    }
                                    sx={{
                                        margin: 0,
                                        flex: 1,
                                    }}
                                />
                                {endAdornment}
                            </Box>
                        </Box>
                        {(fieldError || helperText || error) && (
                            <FormHelperText error={!!(fieldError || error)}>
                                {fieldError?.message || helperText}
                            </FormHelperText>
                        )}
                    </FormGroup>
                );
            }}
        />
    );
}


